import LanguageSelector from "../components/common/LanguageSelector";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  UsersRound,
  Mail,
  CalendarDays,
  CalendarClock,
  DoorOpen,
  Building2,
  ContactRound,
  ShieldCheck,
  Settings2,
  LogOut,
  Menu,
  X,
  Layers3,
  ChevronLeft,
  ChevronRight,
  PanelsTopLeft,
} from "lucide-react";
import { features, roleLabel } from "../config/constants/features";
import { useAuth } from "../store/AuthContext";
import { Avatar, ErrorNotice } from "../components/common/Primitives";
import { apiUrl } from "../config/environment";
import ThemeToggle from "../components/common/ThemeToggle";
import pentabellBlue from "../assets/brand/pentabell-blue-safe.png";
import pentabellWhite from "../assets/brand/pentabell-white-safe.png";
const sidebarStorageKey = "pentaadmin.sidebarCollapsed";
const organizationOrder = [
  "employees",
  "departments",
  "roles",
  "floors",
  "meeting-rooms",
];
const icons = {
  visits: UsersRound,
  correspondence: Mail,
  interviews: CalendarDays,
  "meeting-reservations": CalendarClock,
  employees: ContactRound,
  departments: Building2,
  floors: Layers3,
  "meeting-rooms": DoorOpen,
  users: ShieldCheck,
  roles: ShieldCheck,
};
export default function WorkspaceLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(sidebarStorageKey) === "true",
  );
  const [error, setError] = useState("");
  const location = useLocation();
  const current = features.find((f) =>
    location.pathname.startsWith("/" + f.key),
  );
  const name = `${user!.firstName} ${user!.lastName}`;
  useEffect(() => {
    localStorage.setItem(sidebarStorageKey, String(collapsed));
  }, [collapsed]);
  return (
    <div className={"app-shell" + (collapsed ? " sidebar-collapsed" : "")}>
      {open && (
        <button
          className="sidebar-scrim"
          aria-label={t("Close navigation")}
          onClick={() => setOpen(false)}
        />
      )}
      <aside className={"sidebar " + (open ? "open" : "")}>
        <div className="sidebar-brand-row">
        <Link
          to="/"
          className="brand"
          aria-label={t("PentaOffice")}
          onClick={() => setOpen(false)}
        >
          <span className="brand-icon">
            <img className="brand-logo brand-logo-light" src={pentabellBlue} alt="" />
            <img className="brand-logo brand-logo-dark" src={pentabellWhite} alt="" />
          </span>
          <span className="brand-copy">
            {t("PentaOffice")}
          </span>
        </Link>
        <button
          type="button"
          className="sidebar-collapse"
          aria-label={t(collapsed ? "Expand sidebar" : "Collapse sidebar")}
          title={t(collapsed ? "Expand sidebar" : "Collapse sidebar")}
          onClick={() => setCollapsed((current) => !current)}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        </div>
        <p className="nav-label">{t("WORKSPACE")}</p>
        <nav aria-label={t("Main navigation")}>
          <NavLink
            to="/"
            end
            data-tooltip={t("Overview")}
            onClick={() => setOpen(false)}
          >
            <LayoutDashboard size={19} />
            <span className="nav-text">{t("Overview")}</span>
          </NavLink>
          {user!.role !== "Admin" && (
            <NavLink
              to="/my-activity"
              data-tooltip={t("My activity")}
              onClick={() => setOpen(false)}
            >
              <PanelsTopLeft size={19} />
              <span className="nav-text">{t("My activity")}</span>
            </NavLink>
          )}
          {features.filter((feature) => !feature.organization).map(
            (f) =>
              (user!.role === "Admin" ||
                !f.employeeVisible ||
                f.key === "meeting-reservations") &&
              (f.employeeVisible || f.roles.includes(user!.role)) && (
                <NavLink
                  key={f.key}
                  to={"/" + f.key}
                  data-tooltip={t(f.title)}
                  onClick={() => setOpen(false)}
                >
                  {(() => {
                    const Icon = icons[f.key as keyof typeof icons];
                    return <Icon size={19} />;
                  })()}
                  <span className="nav-text">{t(f.title)}</span>
                </NavLink>
              ),
          )}
          {user!.role === "Admin" && (
            <>
              <p className="nav-label">{t("ORGANIZATION")}</p>
              {features
                .filter((feature) => feature.organization)
                .sort(
                  (a, b) =>
                    organizationOrder.indexOf(a.key) -
                    organizationOrder.indexOf(b.key),
                )
                .map((f) => {
                  const Icon = icons[f.key as keyof typeof icons];
                  return (
                    <NavLink
                      key={f.key}
                      to={"/" + f.key}
                      data-tooltip={t(f.title)}
                      onClick={() => setOpen(false)}
                    >
                      <Icon size={19} />
                      <span className="nav-text">{t(f.title)}</span>
                    </NavLink>
                  );
                })}
            </>
          )}
        </nav>
        <footer className="sidebar-footer">
          <div className="sidebar-footer-controls">
            <ThemeToggle />
            <LanguageSelector />
          </div>
          <span>© {new Date().getFullYear()} {t("PentaOffice")}</span>
        </footer>
      </aside>
      <div className="workspace-main">
        <header className="topbar">
          <div className="breadcrumb">
            <button
              className="icon-button mobile-menu"
              aria-label={t("Open navigation")}
              onClick={() => setOpen(true)}
            >
              {open ? <X /> : <Menu />}
            </button>
            <span>{t("Workspace")}</span>
            <span className="slash">/</span>
            <strong>
              {t(
                current?.title ||
                  (location.pathname === "/profile"
                    ? "My profile"
                    : location.pathname === "/my-activity"
                      ? "My activity"
                    : "Overview"),
              )}
            </strong>
          </div>
          <div className="topbar-right">
            <Link
              to="/profile"
              className="topbar-account"
              aria-label={t("Open my profile")}
            >
              <Avatar name={name} small src={user!.googlePhotoUrl || (user!.attachmentId ? apiUrl("/files/" + user!.attachmentId) : undefined)} />
              <span>
                <strong>{name}</strong>
                <small>{t(roleLabel[user!.role] || user!.role)}</small>
              </span>
            </Link>
            <Link
              to="/profile"
              className="icon-button topbar-settings"
              title={t("My profile & settings")}
              aria-label={t("My profile & settings")}
            >
              <Settings2 size={17} />
            </Link>
            <button
              className="icon-button"
              title={t("Sign out")}
              aria-label={t("Sign out")}
              onClick={() => logout().catch((e) => setError(e.message))}
            >
              <LogOut size={17} />
            </button>
          </div>
        </header>
        <main className="main-content">
          {error && <ErrorNotice message={error} />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
